import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal, Kicker } from "@/components/site/Reveal";
import { ConsentNotice, PrivacyMention } from "@/components/site/LeadForms";
import { usePageMeta } from "@/lib/seo";
import { trackClick } from "@/lib/analytics";
import { trackLinkedInConversion, LINKEDIN_CONVERSIONS } from "@/lib/linkedinConversions";
import { downloadWhitepaper, WHITEPAPER_TITLE, WHITEPAPER_SUBTITLE } from "@/lib/whitepaper";
import { Download, CheckCircle2, ArrowRight, Layers, GitBranch, UserCog, Map, Gauge } from "lucide-react";

const BLOCAGES = [
  { icon: Layers, n: "01", title: "La gouvernance étouffe l'agilité", text: "Comités et validations s'accumulent : la décision ralentit et l'agilité des équipes se dilue." },
  { icon: GitBranch, n: "02", title: "Les dépendances, vrai goulot d'étranglement", text: "Ce n'est pas leur existence mais leur invisibilité et leur mauvaise gestion qui freinent la livraison." },
  { icon: UserCog, n: "03", title: "Les Product Owners « hommes-orchestres »", text: "Le PO absorbe arbitrages, décisions et dépendances : surchargé, il devient lui-même un goulot." },
  { icon: Map, n: "04", title: "La roadmap : communication au lieu de pilotage", text: "Mêlant engagements, hypothèses et urgences, elle rassure au lieu de guider la valeur." },
  { icon: Gauge, n: "05", title: "On mesure l'activité, pas la performance", text: "Tickets traités et sprints respectés ne disent rien de la valeur réellement délivrée." },
];

const SOMMAIRE = [
  "Le constat : « Nous sommes Agile… alors pourquoi ça ne marche pas ? »",
  "Les 5 blocages invisibles : gouvernance, dépendances, PO, roadmaps, indicateurs",
  "Une organisation peut-elle vraiment devenir plus agile ? Agir sur le système",
  "Diagnostiquer sa maturité : un modèle en 5 dimensions et 4 niveaux",
  "Passer du constat à l'action : une démarche pragmatique en 4 étapes",
];

const EMPTY = { nom: "", prenom: "", entreprise: "", fonction: "", email: "", website: "" };

const DownloadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Conversion LinkedIn : une seule fois, à l'entrée dans l'état de confirmation.
  const convFired = useRef(false);
  useEffect(() => {
    if (done && !convFired.current) {
      convFired.current = true;
      trackLinkedInConversion(LINKEDIN_CONVERSIONS.whitepaper);
    }
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { ...form, lead_type: "whitepaper", message: "Téléchargement livre blanc (page dédiée)" });
      setDone(true);
      trackClick("whitepaper_page_download");
      downloadWhitepaper();
      toast.success("Merci ! Le téléchargement de votre livre blanc démarre.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="text-center py-10" data-testid="whitepaper-page-success">
        <CheckCircle2 size={40} className="text-teal-400 mx-auto" />
        <p className="mt-4 font-heading text-lg font-semibold text-slate-100">C'est parti !</p>
        <p className="mt-1 text-sm text-slate-400">Votre livre blanc a été envoyé par email et le téléchargement a démarré.</p>
        <button onClick={downloadWhitepaper} className="btn-primary mt-6" data-testid="whitepaper-page-download-again">
          <Download size={15} /> Télécharger à nouveau
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="whitepaper-page-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="wp-nom" className="input-dark" required placeholder="Nom *" value={form.nom} onChange={set("nom")} />
        <input data-testid="wp-prenom" className="input-dark" required placeholder="Prénom *" value={form.prenom} onChange={set("prenom")} />
        <input data-testid="wp-entreprise" className="input-dark" required placeholder="Entreprise *" value={form.entreprise} onChange={set("entreprise")} />
        <input data-testid="wp-fonction" className="input-dark" required placeholder="Fonction (DSI, Directeur Produit…) *" value={form.fonction} onChange={set("fonction")} />
        <input data-testid="wp-email" className="input-dark" type="email" required placeholder="E-mail professionnel *" value={form.email} onChange={set("email")} />
      </div>
      <input name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <button data-testid="wp-submit-btn" disabled={busy} className="btn-primary w-full disabled:opacity-60">
        {busy ? "Préparation…" : "Télécharger le livre blanc"} <Download size={15} />
      </button>
      <PrivacyMention withPdf />
      <ConsentNotice />
    </form>
  );
};

export default function WhitepaperPage() {
  usePageMeta(
    "Livre blanc — Agile… mais toujours lent ?",
    "Téléchargez notre livre blanc : les 5 blocages invisibles qui freinent la performance des organisations agiles, et comment agir."
  );

  return (
    <main className="bg-[#0b0f19] min-h-screen" data-testid="whitepaper-page">
      <Nav />

      {/* HERO */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 noise overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Kicker>Livre blanc gratuit · ASAP Technologies × Reqen</Kicker>
              <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-50 leading-[1.05]">
                Agile… <span className="grad-text">mais toujours lent ?</span>
              </h1>
              <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">
                Vos équipes itèrent, les rituels tournent, les PO sont en place. Pourtant la performance ne suit pas.
                Le frein n'est peut-être plus l'agilité des équipes — il est devenu <strong className="text-slate-100">organisationnel</strong>.
              </p>
              <p className="mt-4 text-slate-400 text-sm max-w-xl leading-relaxed">
                Ce livre blanc décrypte les <strong className="text-teal-300">5 blocages invisibles</strong> qui freinent réellement
                la performance des organisations agiles — et donne une démarche concrète pour les lever.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href="#telecharger" data-testid="wp-hero-cta" className="btn-primary">
                  <Download size={16} /> Télécharger le livre blanc
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative mx-auto max-w-sm">
                <div className="absolute -inset-4 grad-line opacity-20 blur-2xl" aria-hidden="true" />
                <div className="relative border border-white/10 bg-[#111827] p-8 shadow-2xl rotate-[-1.5deg]">
                  <div className="font-mono2 text-[11px] tracking-[0.25em] uppercase grad-text">Livre blanc</div>
                  <div className="mt-6 font-heading text-3xl font-semibold text-slate-50 leading-tight">
                    Agile… mais toujours lent ?
                  </div>
                  <p className="mt-4 text-sm text-slate-400">{WHITEPAPER_SUBTITLE}</p>
                  <div className="mt-8 space-y-2">
                    {["Gouvernance", "Dépendances", "Product Owners", "Roadmaps", "Indicateurs"].map((t) => (
                      <div key={t} className="flex items-center gap-2 text-xs text-slate-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal-400" /> {t}
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 pt-5 border-t border-white/10 font-mono2 text-[10px] tracking-[0.2em] uppercase text-slate-500">
                    ASAP Technologies × Reqen
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LES 5 BLOCAGES */}
      <section className="py-16 md:py-24 bg-[#0d1322] noise">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker>Ce que vous allez découvrir</Kicker>
            <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50 max-w-2xl">
              Les 5 blocages invisibles qui freinent votre performance.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {BLOCAGES.map((b, i) => (
              <Reveal key={b.n} delay={i * 0.06}>
                <div className="h-full border border-white/10 bg-[#131b2c] p-7" data-testid={`blocage-${b.n}`}>
                  <div className="flex items-center justify-between">
                    <b.icon size={22} className="text-teal-300" />
                    <span className="font-mono2 text-xs tracking-[0.2em] text-slate-600">{b.n}</span>
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-slate-50">{b.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.36}>
              <a href="#telecharger" className="h-full border border-teal-400/30 bg-teal-400/5 p-7 flex flex-col justify-center group" data-testid="blocage-cta">
                <span className="font-heading text-lg font-semibold text-slate-50">Et surtout : comment les lever.</span>
                <span className="mt-3 inline-flex items-center gap-2 text-sm text-teal-300">
                  Télécharger le livre blanc <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SOMMAIRE + POUR QUI */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <Kicker>Au sommaire</Kicker>
            <ul className="mt-6 space-y-4">
              {SOMMAIRE.map((s, i) => (
                <li key={i} className="flex gap-4 text-sm text-slate-300 leading-relaxed">
                  <span className="font-mono2 text-teal-400 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {s}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <Kicker>Pour qui ?</Kicker>
            <p className="mt-6 text-slate-300 text-sm leading-relaxed">
              Dirigeants, managers, responsables Agile, Product Owners, chefs de projet — toute organisation
              ayant adopté Scrum, Kanban ou SAFe et qui peine à accélérer malgré ses efforts.
            </p>
            <div className="mt-6 border-l-2 border-teal-400/50 pl-5">
              <p className="font-heading text-lg text-slate-100 italic leading-relaxed">
                « Avant de transformer davantage, comprenez ce qui bloque réellement. »
              </p>
            </div>
            <div className="mt-8 space-y-3">
              {["Identifier ce qui freine vraiment votre organisation", "Prioriser où agir pour retrouver de la fluidité", "Engager des transformations ciblées, pas cosmétiques"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm text-slate-400">
                  <CheckCircle2 size={16} className="text-teal-400 shrink-0" /> {t}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORMULAIRE DE TELECHARGEMENT */}
      <section id="telecharger" className="py-16 md:py-24 bg-[#0d1322] noise scroll-mt-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="border border-white/10 bg-[#111827] p-7 sm:p-10">
              <Kicker>Téléchargement gratuit</Kicker>
              <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-semibold text-slate-50">
                Recevez le livre blanc
              </h2>
              <p className="mt-3 text-sm text-slate-400">
                Renseignez vos coordonnées : le PDF s'ouvre immédiatement et vous est aussi envoyé par email.
              </p>
              <div className="mt-8">
                <DownloadForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
